import { IconPlus } from "@tabler/icons-react";
import { Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Switch } from "../../../../components/common/switch/switch";
import { SectionCard } from "../../../../components/common/section-card";
import { RequiredMark } from "../../../../components/common/required-mark";
import { OptionCard, type VariantOption, type VariantOptionAxis } from "./option-card";
import { VariantPricingRow, type VariantRowEdit } from "./variant-pricing-row";
import type { NonEmptyVariantAxis } from "./variant-axis-registry";

export interface ResolvedVariant {
  /** Stable key for the combination, used to index variantEdits. */
  key: string;
  label: string;
  /** Per-axis attribute-value IDs that make up this combination. */
  axisValues: Partial<Record<NonEmptyVariantAxis, number>>;
}

interface Props {
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
  options: VariantOption[];
  onOptionRemove: (id: number) => void;
  onOptionUpdate: (id: number, patch: Partial<VariantOption>) => void;
  onOptionAdd: () => void;
  onLabelsResolved: (axis: VariantOptionAxis, labels: Record<string, string>) => void;
  variants: ResolvedVariant[];
  variantEdits: Record<string, VariantRowEdit>;
  onVariantEditUpdate: (key: string, patch: Partial<VariantRowEdit>) => void;
  onVariantRemove: (key: string) => void;
  activeOptionsCount: number;
  /** 0..100 per variant key while submit-time uploads are in flight. */
  uploadProgress?: Record<string, number>;
  readOnly?: boolean;
  fieldErrors?: Set<string>;
  /** Category policy makes Condition mandatory for every variant. */
  conditionRequired?: boolean;
}

export function VariantsBuilderSection({
  enabled, onEnabledChange,
  options, onOptionRemove, onOptionUpdate, onOptionAdd, onLabelsResolved,
  variants, variantEdits, onVariantEditUpdate, onVariantRemove,
  activeOptionsCount,
  uploadProgress,
  readOnly = false,
  fieldErrors,
  conditionRequired = false,
}: Props) {
  const columns: { label: string; required?: boolean }[] = [
    { label: "Image" },
    { label: "Variant" },
    { label: "SKU", required: true },
    { label: "Stock", required: true },
    { label: "Condition", required: conditionRequired },
    { label: "" },
  ];

  return (
    <>
      <SectionCard
        shadow
        title="Variants"
        subtitle={enabled ? "Define variant options (RAM, Storage). Each combination creates a unique SKU with its own price and stock." : undefined}
        action={
          <Switch
            checked={enabled}
            onChange={onEnabledChange}
            aria-label="Enable variants"
            disabled={readOnly}
          />
        }
        className="gap-5"
      >
        {enabled && (
          <div className="flex flex-col gap-4">
            {options.map((opt, i) => (
              <OptionCard
                key={opt.id}
                option={opt}
                index={i}
                onRemove={() => onOptionRemove(opt.id)}
                onUpdate={(patch) => onOptionUpdate(opt.id, patch)}
                onLabelsResolved={onLabelsResolved}
                readOnly={readOnly}
              />
            ))}
            {!readOnly && (
              <Button
                variant="secondaryDark"
                size="sm"
                leftIcon={<IconPlus size={14} />}
                className="self-start"
                onClick={onOptionAdd}
              >
                Add variant
              </Button>
            )}
          </div>
        )}
      </SectionCard>

      {enabled && variants.length > 0 && (
        <SectionCard shadow title="Variants & pricing" className="gap-5">
          <Text variant="bodySm" tone="dark">
            {variants.length} variants {readOnly ? "submitted" : "generated"} from {activeOptionsCount} {activeOptionsCount === 1 ? "option" : "options"}
          </Text>
          <div className="overflow-hidden rounded-xl border border-borderDefault">
            {/* Phones: keep the columns readable and scroll the table sideways. */}
            <div className="overflow-x-auto">
            <div className="max-md:min-w-[680px]">
            <div className="grid grid-cols-[100px_1fr_1fr_1fr_1fr_36px] gap-3 bg-bgLight px-6 py-3">
              {columns.map((col, i) => (
                <Text key={i} variant="capsBig" tone="dark">
                  {col.label}
                  {col.required && !readOnly && <RequiredMark />}
                </Text>
              ))}
            </div>
            {variants.map((v) => (
              <VariantPricingRow
                key={v.key}
                variantKey={v.key}
                variant={v.label}
                edit={variantEdits[v.key] ?? { sku: "", stock: "", pricingTiers: [{ id: 1, minQty: "1", price: "" }] }}
                onUpdate={(patch) => onVariantEditUpdate(v.key, patch)}
                onRemove={() => onVariantRemove(v.key)}
                uploadProgress={uploadProgress?.[v.key]}
                readOnly={readOnly}
                fieldErrors={fieldErrors}
              />
            ))}
            </div>
            </div>
          </div>
        </SectionCard>
      )}
    </>
  );
}
